import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ExternalLink, Link2, Plus, RefreshCw, Search, X } from "lucide-react";
import { montageApi, type AccountPost, type WaitingItem } from "../../api";
import { Button } from "../../components/ui/button";
import { FilterChip } from "../../components/ui/primitives";
import { byPeople, GROUP_OF, ME_EDITOR, PROJECTS } from "../../data/people";
import { useAccountInfo } from "../../lib/accounts";
import { notify } from "../../lib/notify";
import { useSafeSearchParams } from "../../lib/urlState";
import { readRemembered, writeRemembered } from "../../../../shared/lib/remembered";
import { confirmDialog } from "../../../../shared/ui/ConfirmHost";
import { cn, plural } from "../../lib/utils";
import { useStore } from "../../store";
import type { ItemStatus } from "../../types";

/**
 * «Посты» (23.10.2026): лента аккаунта направления — от поста к ролику.
 *
 * Всё, что выходит на аккаунтах направлений, выкладывают те же монтажёры, а
 * ссылку к ролику прикладывают не всегда. Здесь видно, что уже вышло и к чему
 * приложено; у свободного поста выбираешь свой ролик — и он закрывается.
 *
 * С 25.10.2026 ролик берётся откуда угодно: с любого шага (пост выходит и до
 * приёмки), из любого аккаунта (выложили не туда, куда ставили — аккаунт
 * ролика поправится сам), с поиском по названию. А если ролика в «Монтаже»
 * нет вовсе — пост присваивается себе, тема заводится задним числом.
 */

const when = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Almaty" });

const postKey = (link: string) =>
  /instagram\.com\/(?:[^/]+\/)?(?:p|reel|reels|tv)\/([A-Za-z0-9_-]{5,})/i.exec(link)?.[1]
  ?? /tiktok\.com\/(?:[^?#]*\/)?video\/([0-9]{8,})/i.exec(link)?.[1] ?? null;

const STEP: Record<ItemStatus, string> = {
  assigned: "не сдан",
  uploaded: "на проверке",
  rework: "на доработке",
  moderated: "ждёт приёмки",
  approved: "принят",
  published: "вышел",
  cancelled: "снят",
};

/** Аккаунты направлений монтажёра — те же, куда ему ставят публикации. */
function myAccounts(): string[] {
  const group = GROUP_OF[ME_EDITOR];
  const mine = PROJECTS.filter((p) => (byPeople(p) ? p.editorIds!.includes(ME_EDITOR) : Boolean(group && p.groupIds.includes(group))));
  return [...new Set(mine.flatMap((p) => p.accountIds ?? []))].filter((id) => !id.startsWith("yt:"));
}

function AccountChip({ id, active, onClick }: { id: string; active: boolean; onClick: () => void }) {
  const info = useAccountInfo(id);
  return <FilterChip active={active} onClick={onClick}>{info?.platform === "tiktok" ? `TikTok · ${info.name}` : info?.name ?? id}</FilterChip>;
}

const lower = (s: string) => s.toLocaleLowerCase("ru-RU").replace(/ё/g, "е");

/**
 * Ролики к посту: подсказка сверки, затем поставленные в этот аккаунт, затем
 * свои, затем ближе по времени. Поиск — по словам названия, в любом порядке.
 */
export function rankFor(post: AccountPost, pending: WaitingItem[], query = ""): WaitingItem[] {
  const key = postKey(post.link);
  const at = Date.parse(post.publishedAt);
  const words = lower(query).split(/\s+/).filter(Boolean);
  const time = (i: WaitingItem) => Date.parse(i.approvedAt ?? i.createdAt ?? post.publishedAt);
  return pending
    .filter((i) => !key || !i.rejected.includes(key))
    .filter((i) => words.every((w) => lower(i.title).includes(w)))
    .sort((a, b) => (b.match[post.mediaId] ?? 0) - (a.match[post.mediaId] ?? 0)
      || Number(b.here) - Number(a.here)
      || Number(b.mine) - Number(a.mine)
      || Math.abs(time(a) - at) - Math.abs(time(b) - at));
}

/**
 * Черновик названия из текста обложки. Модель отдаёт его заглавными и с
 * мусором вокруг («QAZAQSTA 0000 ТОКАЕВ ПРИЛЕТЕЛ…») — монтажёр поправит, но
 * начинать с готовой фразы быстрее, чем с пустого поля.
 */
export function titleFromCover(cover: string | null | undefined): string {
  const words = String(cover ?? "").replace(/\s+/g, " ").trim().split(" ")
    // Цифры и латиница заглавными — водяные знаки и логотипы, не название.
    .filter((w) => !/^[\d\p{P}\p{S}]+$/u.test(w) && !/^[A-Z]{3,}$/.test(w));
  const text = words.join(" ").slice(0, 120).trim();
  if (!text) return "";
  const loud = text === text.toLocaleUpperCase("ru-RU");
  const body = loud ? text.toLocaleLowerCase("ru-RU") : text;
  return body.charAt(0).toLocaleUpperCase("ru-RU") + body.slice(1);
}

function WaitingRow({ item, post, disabled, onPick }: { item: WaitingItem; post: AccountPost; disabled: boolean; onPick: () => void }) {
  const elsewhere = useAccountInfo(item.here ? null : item.accountId);
  const match = item.match[post.mediaId];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPick}
      className="flex items-center gap-3 rounded-[14px] bg-muted/60 px-3 py-2 text-left hover:bg-muted disabled:opacity-50"
    >
      <Link2 className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium">{item.title}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {item.mine ? "ваш" : item.editor ?? "без монтажёра"} · {STEP[item.status]}
          {item.status === "approved" && item.approvedAt ? ` ${when(item.approvedAt)}` : ""}
          {elsewhere ? ` · ставили в ${elsewhere.name}` : ""}
        </span>
      </span>
      {match != null && <span className="shrink-0 text-xs font-medium text-publish-fg">обложка {Math.round(match * 100)}%</span>}
    </button>
  );
}

/** «Ролика нет в списке»: свой ролик, выложенный мимо «Монтажа», — тема задним числом. */
function ClaimPost({ post, busy, onClaim }: { post: AccountPost; busy: boolean; onClaim: (title: string) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(() => titleFromCover(post.cover));
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-1.5 self-start text-[13px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground">
        <Plus className="size-3.5" /> Ролика нет в списке — это мой ролик без публикации
      </button>
    );
  }
  return (
    <form
      className="flex flex-col gap-2 rounded-[14px] border border-dashed p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim().length >= 3) onClaim(title.trim());
      }}
    >
      <span className="text-[13px] font-semibold">Свой ролик без публикации</span>
      <span className="text-xs text-muted-foreground">Заведём публикацию задним числом — сразу выполненную, автор вы. Пост попадёт в «Видео» и вашу статистику.</span>
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Название ролика"
          className="h-9 min-w-0 flex-1 rounded-full border border-input bg-background px-3.5 text-[13px] outline-none focus:ring-4 focus:ring-ring/20"
        />
        <Button type="submit" size="sm" disabled={busy || title.trim().length < 3}>Присвоить себе</Button>
      </div>
    </form>
  );
}

const REMEMBER_ACCOUNT = "montage:posts:account";

/**
 * Аккаунт ленты: из ссылки «Найти пост» (`?account=&item=`), иначе прошлый
 * выбор, иначе первый. `item` — ролик, к которому ищут пост: похожие по
 * обложке посты подсвечены, а в списке «Какой это ролик?» он первый.
 */
export function AccountPosts({ className }: { className?: string }) {
  const accounts = useMemo(myAccounts, []);
  const refreshBoard = useStore((s) => s.refresh);
  const [params] = useSafeSearchParams();
  const wantItem = params.get("item");
  const [account, setAccountState] = useState<string | null>(() => {
    const fromUrl = params.get("account");
    if (fromUrl && accounts.includes(fromUrl)) return fromUrl;
    const last = readRemembered<string | null>(REMEMBER_ACCOUNT, null);
    return last && accounts.includes(last) ? last : accounts[0] ?? null;
  });
  const setAccount = (id: string) => {
    setAccountState(id);
    writeRemembered(REMEMBER_ACCOUNT, id);
  };
  const [data, setData] = useState<{ posts: AccountPost[]; pending: WaitingItem[]; syncedAt: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [freeOnly, setFreeOnly] = useState(true);
  const [picking, setPicking] = useState<AccountPost | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const accountName = useAccountInfo(account)?.name ?? "этот аккаунт";

  const load = useCallback(() => {
    if (!account) return;
    setError(null);
    montageApi.accountPosts(account).then(setData).catch((e) => setError((e as Error).message));
  }, [account]);
  useEffect(() => { setData(null); setPicking(null); load(); }, [load]);
  useEffect(() => { setQuery(""); }, [picking?.mediaId]);

  const done = (text: string) => {
    notify(text, { icon: "check" });
    setPicking(null);
    load();
    void refreshBoard();
  };

  const attach = async (post: AccountPost, item: WaitingItem) => {
    const notes = [
      item.status !== "approved" && `Ролик ещё ${STEP[item.status]} — пост закроет его как вышедший.`,
      !item.here && `Аккаунт ролика сменится на ${accountName}.`,
    ].filter(Boolean).join("\n");
    if (!(await confirmDialog({ title: `Приложить этот пост к «${item.title}»?`, description: notes || undefined, confirmText: "Приложить" }))) return;
    setBusy(true);
    try {
      await montageApi.publishLink(item.taskId, item.itemId, post.link);
      done(`«${item.title}» — ссылка приложена`);
    } catch (e) {
      notify((e as Error).message, { icon: "rework" });
    } finally {
      setBusy(false);
    }
  };

  const claim = async (post: AccountPost, title: string) => {
    setBusy(true);
    try {
      await montageApi.claimPost(post.mediaId, title);
      done(`«${title}» — пост ваш, публикация заведена`);
    } catch (e) {
      notify((e as Error).message, { icon: "rework" });
    } finally {
      setBusy(false);
    }
  };

  if (!accounts.length) {
    return <p className={cn("text-sm text-muted-foreground", className)}>У ваших направлений нет аккаунтов Instagram или TikTok.</p>;
  }

  const posts = (data?.posts ?? []).filter((p) => !freeOnly || !p.linked);
  const free = (data?.posts ?? []).filter((p) => !p.linked).length;
  const mineWaiting = (data?.pending ?? []).filter((i) => i.mine && i.status === "approved").length;
  const rankedAll = picking ? rankFor(picking, data?.pending ?? [], query) : [];
  // Пришли за постом конкретного ролика — он первым.
  const ranked = wantItem ? [...rankedAll.filter((i) => i.itemId === wantItem), ...rankedAll.filter((i) => i.itemId !== wantItem)] : rankedAll;
  const target = wantItem ? (data?.pending ?? []).find((i) => i.itemId === wantItem) : undefined;
  const looksLike = (p: AccountPost) => target?.match[p.mediaId] != null;

  return (
    <div className={cn("flex min-h-0 flex-col gap-4", className)}>
      <div className="flex flex-wrap items-center gap-2">
        {accounts.map((id) => <AccountChip key={id} id={id} active={account === id} onClick={() => setAccount(id)} />)}
        <span className="mx-1 h-5 w-px bg-border" />
        <FilterChip active={freeOnly} onClick={() => setFreeOnly(!freeOnly)} count={free}>Без ролика</FilterChip>
        <span className="flex-1" />
        <button type="button" onClick={load} className="flex items-center gap-1.5 text-[13px] text-muted-foreground" aria-label="Обновить ленту">
          <RefreshCw className="size-4" /> Обновить
        </button>
      </div>

      {data && (
        <p className="text-[13px] text-muted-foreground">
          {mineWaiting > 0 ? `${mineWaiting} ${plural(mineWaiting, ["ваш принятый ролик ждёт", "ваших принятых ролика ждут", "ваших принятых роликов ждут"])} ссылку — нажмите на вышедший пост и выберите ролик.` : "Нажмите на пост без ролика и выберите, чей он."}
          {data.syncedAt ? ` Лента на ${when(data.syncedAt)}, обновляется раз в 15 минут.` : ""}
        </p>
      )}
      {error && <p className="text-sm text-rework-fg">Не удалось загрузить: {error}</p>}
      {target && (
        <p className="rounded-[14px] bg-publish-soft px-3.5 py-2 text-[13px] text-publish-fg">
          Ищем пост для «{target.title}»{data?.posts.some(looksLike) ? " — похожие по обложке подсвечены" : ""}. Нажмите на вышедший пост.
        </p>
      )}

      {picking && (
        <div className="flex flex-col gap-3 rounded-[20px] border bg-card p-4">
          <div className="flex items-start gap-3">
            {picking.thumbnail && <img src={picking.thumbnail} alt="" referrerPolicy="no-referrer" className="h-24 w-[54px] shrink-0 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Какой это ролик?</div>
              <a href={picking.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[13px] text-muted-foreground underline underline-offset-2">
                пост от {when(picking.publishedAt)} <ExternalLink className="size-3" />
              </a>
            </div>
            <button type="button" onClick={() => setPicking(null)} aria-label="Закрыть" className="text-muted-foreground hover:text-foreground"><X className="size-4" /></button>
          </div>
          {(data?.pending.length ?? 0) > 0 && (
            <label className="flex h-9 items-center gap-2 rounded-full border border-input bg-background px-3.5 focus-within:ring-4 focus-within:ring-ring/20">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Найти ролик по названию — любой аккаунт, любой шаг"
                className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
              />
            </label>
          )}
          {ranked.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">{query ? "Ничего не нашлось." : "Роликов без ссылки нет."}</p>
          ) : (
            <div className="flex max-h-[320px] flex-col gap-1.5 overflow-y-auto">
              {ranked.map((item) => (
                <WaitingRow key={item.itemId} item={item} post={picking} disabled={busy} onPick={() => void attach(picking, item)} />
              ))}
            </div>
          )}
          <ClaimPost key={picking.mediaId} post={picking} busy={busy} onClaim={(title) => void claim(picking, title)} />
        </div>
      )}

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto pb-6">
        {!data && !error ? (
          <p className="text-sm text-muted-foreground">Загружаем ленту…</p>
        ) : posts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{freeOnly ? "Все посты с начала прошлого месяца уже приложены к роликам." : "С начала прошлого месяца постов нет."}</p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(132px,1fr))] gap-2.5">
            {posts.map((p) => (
              <button
                key={p.mediaId}
                type="button"
                disabled={Boolean(p.linked)}
                onClick={() => setPicking(p)}
                title={p.linked ? `Приложен к «${p.linked.title}»` : "Выбрать ролик к этому посту"}
                className={cn(
                  "flex flex-col overflow-hidden rounded-[14px] border bg-background text-left transition-shadow",
                  p.linked ? "cursor-default opacity-70" : "hover:ring-4 hover:ring-ring/20",
                  picking?.mediaId === p.mediaId && "ring-2 ring-primary",
                  !p.linked && looksLike(p) && picking?.mediaId !== p.mediaId && "ring-2 ring-publish",
                )}
              >
                <div className="aspect-[9/16] w-full bg-muted">
                  {p.thumbnail && (
                    <img src={p.thumbnail} alt="" loading="lazy" referrerPolicy="no-referrer" className="size-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                </div>
                <div className="flex flex-col gap-0.5 px-2 py-1.5">
                  <span className="text-[11px] text-muted-foreground">
                    {when(p.publishedAt)}{p.views != null ? ` · ${p.views.toLocaleString("ru-RU")} просм.` : ""}
                  </span>
                  {p.linked ? (
                    <span className="flex items-center gap-1 truncate text-xs font-medium text-done-fg">
                      <Check className="size-3 shrink-0" />
                      <span className="truncate">{p.linked.mine ? "ваш" : p.linked.editor ?? "приложен"}{p.linked.auto ? " · сверка" : ""}</span>
                    </span>
                  ) : (
                    <span className="text-xs font-medium text-publish-fg">без ролика</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
